import { QuestionRegulatoryData, QuestionType, Questionnaire, QuestionnaireQuestion, SupplierQuestionnaireData, SurveyResponseModel } from "../../Models/Surveys/SurveyDataModel";
import { BaseRegulationLogicManager } from "../BaseRegulationLogicManager";
import { ComplianceScoreResult, InventoryDetailsResponse } from "../../StateControllers/Survey/SurveyResponse/SurveyResponseModels";
import { RegulationManager } from "../RegulationManager";

export class ConflictMineralsV2CustomLogicManager extends BaseRegulationLogicManager {
    public static ConflictMineralsV2RegulationId = "e1d5cb06-e7ce-4b11-a741-e72f96e3b739";
    private readonly _surveyResponse : SurveyResponseModel | null;

    constructor(surveyResponse: SurveyResponseModel | null, questions: QuestionnaireQuestion[], overallQuestionnaire: Questionnaire, questionRegulatoryData: QuestionRegulatoryData[], selectedInventory: InventoryDetailsResponse | null, complianceScoreResult: ComplianceScoreResult | null) {
        super(questions, overallQuestionnaire, questionRegulatoryData, selectedInventory, complianceScoreResult);
        this._surveyResponse = surveyResponse;
        questions.forEach(x => x.hidden = false);
    }

    //checks if the cmrt supplier questionnaire is already there and if it has the declaration of scope question in it or not and return its value. 
    //if anything is not present then return null

    protected getSupplierQuestionnaireDeclarationOfScope(): string | null {
      if (!this.getSupplierLevelQuestionnaire()?.questionnaire?.pages) {
          return null;
      }
      
      const cmrtRegulation = this.getSupplierLevelQuestionnaire()?.questionnaire.pages.find(
          page => page.regulationId === RegulationManager.ConflictMineralsV2SupplierQuestionnaireRegulationId
      );
      
      if (!cmrtRegulation?.questions) {
          return null;
      }
      
      const declarationOfScope = cmrtRegulation.questions.find(
          question => question.summaryFieldKey === "cmrt_declaration_of_scope_or_class"
      );
      
      // Check if declaration exists and has a valid value
      if (declarationOfScope) {
          return declarationOfScope.value === "--" ? "" : declarationOfScope.value;
      }
      
      return null;
  }

    //if the supplier questionnaire declaration of scope is either not selected yet or selected as company then it means we dont want to show cmrt in inventory level yet
    private shouldShowConflictMinerals(){
        var declarationOfScope = this.getSupplierQuestionnaireDeclarationOfScope();
        if(declarationOfScope == null){ //this should never be the case in case of CMRT 
            return true;
        }
        if(declarationOfScope == "" || declarationOfScope?.toLowerCase() == "company"){
            return false;
        }
        return true;
    }

    getSupplierLevelQuestionnaire = () => {
        return this._surveyResponse?.supplierQuestionnaireData ?? null;
    }

    protected getAdditionalQuestionIndicesToHide(question: QuestionnaireQuestion, questionIndex: number, indicesToHideOrShow: boolean[]) {
        if(this.shouldShowConflictMinerals()) {
            // First, make all questions visible
            this._questions.forEach(q => q.hidden = false);
            
            // Find the indices of the first two sections
            const sectionIndices = this._questions
              .map((q, index) => q.type === QuestionType.Section ? index : -1)
              .filter(index => index !== -1);
            
            // Get the index of first and second section
            const firstSectionIndex = sectionIndices[0] || 0;
            const secondSectionIndex = sectionIndices[1] || this._questions.length;
            
            // Hide all questions between first and second section
            for (let i = firstSectionIndex; i < secondSectionIndex; ++i) {
              this._questions[i].hidden = true;
            }
            
            // Special case for "User Defined" scope
            if (this.getSupplierQuestionnaireDeclarationOfScope() === "User Defined" || this.getSupplierQuestionnaireDeclarationOfScope() === "Custom") {
              // Find the "Company Info" section index
              const companyInfoSectionIndex = this._questions.findIndex(q => 
                q.type === QuestionType.Section && 
                q.text && 
                q.text.includes("Company Info"));
              
              if (companyInfoSectionIndex !== -1) {
                this._questions[companyInfoSectionIndex].hidden = false;
              }
              
              // Find the "Description of Scope" field index
              const descriptionOfScopeIndex = this._questions.findIndex(q => 
                q.text && 
                q.text.includes("Description of Scope"));
              
              if (descriptionOfScopeIndex !== -1) {
                this._questions[descriptionOfScopeIndex].hidden = false;
                this._questions[descriptionOfScopeIndex].isRequired = true;
              }
            }
            
            return this._questions.map(q => q.hidden);
          }
          
          // If we don't want to show conflict minerals, hide all questions
          return this._questions.map(() => true);
    }

    public getPageRequiredInfoLocalized() {

        //if the page needs to be shown then no help text should be added
        if(this.shouldShowConflictMinerals()) return null;

        const supplierLevelQuestionnaireData = this.getSupplierLevelQuestionnaire();
       
        // Check if this is a revision response BUT the supplier questionnaire part wasn't included in the revision request.
        if (supplierLevelQuestionnaireData !== null &&
            this._surveyResponse?.isRevisionResponse === true &&
            supplierLevelQuestionnaireData.revisionRequest === null) {
            return 'The requestor only reopened the Materials section. To update company-level compliance information, ask them to include those questions in the revision request.';
        }

        var declarationOfScope = this.getSupplierQuestionnaireDeclarationOfScope();

        //if no declaration of scope is selected in supplier questionnaire
        if(declarationOfScope == ""){
            return "You have not indicated a declaration scope on the CMRT questionnaire. To complete the survey, please provide the declaration scope for your CMRT response."
        }
        //if company is selected in supplier questionnaire
        else if (declarationOfScope?.toLowerCase() == "company"){
            return "You’ve selected a Company-level CMRT declaration, so material-specific details are not required. If this selection was made in error, please update your declaration scope in the company-level questionnaire."
        }

        return null;
    }
}
